import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, WarningCircle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { formatEUR, formatDate } from "@/lib/constants";

const mmYYYY = (p) => (p && p.length >= 7 ? `${p.slice(5, 7)}/${p.slice(0, 4)}` : p);

function CheckBox({ chk }) {
  const ok = !chk.ok;
  return (
    <div className={`rounded-md border px-3 py-2 text-[13px] flex gap-2 ${ok ? "border-emerald-200 bg-emerald-50 text-emerald-900" : "border-amber-300 bg-amber-50 text-amber-900"}`}
         data-testid="dunning-precheck-result">
      {ok ? <ShieldCheck size={18} className="shrink-0" /> : <WarningCircle size={18} className="shrink-0" />}
      <div>
        <div className="font-medium">{ok ? "Keine Mahnung zulässig" : "Mahnfähiger Rückstand vorhanden"}</div>
        <div>{chk.reason}</div>
        <div className="text-[11px] opacity-80 mt-0.5">
          Saldo {formatEUR(chk.saldo_eur)} · mahnfähig {formatEUR(chk.dunnable_eur)}
          {chk.surplus_applied_eur > 0 && ` · davon aus Guthaben verrechnet ${formatEUR(chk.surplus_applied_eur)}`}
        </div>
      </div>
    </div>
  );
}

function ClaimsTable({ claims }) {
  if (!claims?.length) return null;
  return (
    <table className="w-full text-[12px]" data-testid="dunning-precheck-claims">
      <thead><tr className="text-slate-500 text-left"><th className="py-1">Forderung</th><th>Fällig</th><th className="text-right">Soll</th><th className="text-right">Offen</th><th className="text-right">Tage</th></tr></thead>
      <tbody>
        {claims.map((c) => (
          <tr key={`${c.period}-${c.category}`} className="border-t border-slate-100">
            <td className="py-1">{c.label} {mmYYYY(c.period)}</td>
            <td>{formatDate(c.due_date)}</td>
            <td className="text-right tabular-nums">{formatEUR(c.soll)}</td>
            <td className="text-right tabular-nums font-medium">{formatEUR(c.open)}</td>
            <td className="text-right tabular-nums">{c.days_overdue}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function CorrectionList({ res }) {
  if (!res.stornos.length && !res.voided_documents.length) {
    return <div className="text-[13px] text-slate-600" data-testid="dunning-correction-none">Alle automatisch erzeugten Mahngebühren und Verzugszinsen sind korrekt — keine Korrektur nötig.</div>;
  }
  return (
    <div className="space-y-2" data-testid="dunning-correction-list">
      {res.stornos.map((s) => (
        <div key={s.posting_id} className="rounded border border-rose-200 bg-rose-50/60 px-2.5 py-1.5 text-[12px]" data-testid={`dunning-storno-${s.posting_id}`}>
          <div className="flex justify-between gap-2 font-medium text-rose-900">
            <span>{s.reference} vom {formatDate(s.posting_date)}</span>
            <span className="tabular-nums">Storno −{formatEUR(s.storno_amount)}{!s.full && " (Teil)"}</span>
          </div>
          <div className="text-rose-800/80">{s.reason}</div>
        </div>
      ))}
      {res.voided_documents.map((d) => (
        <div key={d.document_id} className="rounded border border-slate-200 bg-slate-50 px-2.5 py-1.5 text-[12px]" data-testid={`dunning-void-${d.document_id}`}>
          <span className="font-medium">{d.title}</span> ({formatDate(d.date)}) → wird als „storniert/gegenstandslos" markiert
        </div>
      ))}
    </div>
  );
}

export default function DunningCorrectionDialog({ open, onClose, tenancyId, onDone }) {
  const [chk, setChk] = useState(null);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setChk(null); setRes(null);
    api.get(`/dunning/precheck/${tenancyId}`).then(({ data }) => setChk(data)).catch(() => setChk(false));
    api.post(`/dunning/corrections/${tenancyId}`, { dry_run: true }).then(({ data }) => setRes(data)).catch(() => setRes(false));
  }, [open, tenancyId]);

  const apply = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/dunning/corrections/${tenancyId}`, { dry_run: false });
      toast.success(`${data.stornos.length} Stornobuchung(en) erstellt · ${data.voided_documents.length} Schreiben als gegenstandslos markiert`);
      onDone?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Korrektur fehlgeschlagen");
    } finally { setBusy(false); }
  };

  const pending = res && (res.stornos.length > 0 || res.voided_documents.length > 0);
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="dunning-correction-dialog">
        <DialogHeader>
          <DialogTitle>Mahnungen prüfen & korrigieren</DialogTitle>
          <DialogDescription>Salden- und Zuordnungsprüfung mit Verrechnung aller Zahlungen und Guthaben. Fehlerhafte Mahngebühren/Zinsen werden per Stornobuchung korrigiert — nichts wird gelöscht.</DialogDescription>
        </DialogHeader>
        {(chk === null || res === null) && <div className="text-sm text-slate-500 py-4">Prüfe Mietkonto…</div>}
        {chk === false && <div className="text-sm text-rose-700">Prüfung fehlgeschlagen.</div>}
        {chk && <div className="space-y-3"><CheckBox chk={chk} /><ClaimsTable claims={chk.claims} /></div>}
        {res && <div className="pt-2"><div className="text-[12px] uppercase tracking-wide text-slate-500 mb-1.5">Korrekturen</div><CorrectionList res={res} /></div>}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="dunning-correction-close">Schließen</Button>
          {pending && (
            <Button onClick={apply} disabled={busy} className="bg-rose-700 hover:bg-rose-800" data-testid="dunning-correction-apply">
              {busy ? "Buche…" : "Stornobuchungen erstellen"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
